/**
 * Marketing estimate for the window-cleaning service page calculator.
 * Does not change booking checkout contracts — converts UI inputs into an
 * equivalent window count priced with the public per-window rate.
 */

export type WindowPricingMode = 'type' | 'area';

export type WindowTypeId = 'single' | 'large' | 'glassDoor' | 'frenchSingle' | 'frenchLarge';

export const WINDOW_TYPE_IDS: readonly WindowTypeId[] = [
  'single',
  'large',
  'glassDoor',
  'frenchSingle',
  'frenchLarge',
] as const;

/** Relative weight vs a standard bookable window unit. */
export const WINDOW_TYPE_WEIGHTS: Record<WindowTypeId, number> = {
  single: 1,
  large: 1.5,
  glassDoor: 2,
  frenchSingle: 1.5,
  frenchLarge: 2.5,
};

/** Approximate glass area of one standard window (both sides counted as one unit). */
export const SQM_PER_STANDARD_WINDOW = 1.5;

/** Blinds cleaning as a fraction of the per-window unit price. */
export const BLINDS_SURCHARGE_RATIO = 0.4;

/** Surcharge when glass is cleaned after renovation / construction dust. */
export const AFTER_RENOVATION_MULTIPLIER = 1.3;

export const WINDOW_CALCULATOR_VAT_RATE = 0.2;

export type WindowTypeQuantities = Record<WindowTypeId, { windows: number; blinds: number }>;

/** Per-type row as stored on the booking contract. */
export type WindowTypeQuantity = { windows: number; blinds: number };

export interface WindowAreaQuantities {
  windowSqm: number;
  blindsSqm: number;
}

export interface WindowCalculatorAddOnLine {
  id: string;
  quantity: number;
  unitPrice: number;
}

export interface WindowCalculatorPricingInput {
  mode: WindowPricingMode;
  types: WindowTypeQuantities;
  area: WindowAreaQuantities;
  afterRenovation: boolean;
  pricePerWindow: number;
  minimumWindows: number;
  minimumPrice: number;
  addOns?: WindowCalculatorAddOnLine[];
}

export interface WindowCalculatorEstimate {
  equivalentWindows: number;
  blindsUnits: number;
  bookedWindows: number;
  windowsNet: number;
  blindsNet: number;
  renovationExtraNet: number;
  addOnsNet: number;
  subtotalNet: number;
  vat: number;
  totalGross: number;
  meetsMinimum: boolean;
  isEmpty: boolean;
}

function clampNonNegative(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return value;
}

export function createEmptyWindowTypeQuantities(): WindowTypeQuantities {
  return {
    single: { windows: 0, blinds: 0 },
    large: { windows: 0, blinds: 0 },
    glassDoor: { windows: 0, blinds: 0 },
    frenchSingle: { windows: 0, blinds: 0 },
    frenchLarge: { windows: 0, blinds: 0 },
  };
}

export function sumTypeEquivalentWindows(types: WindowTypeQuantities): {
  equivalentWindows: number;
  blindsUnits: number;
} {
  let equivalentWindows = 0;
  let blindsUnits = 0;

  for (const id of WINDOW_TYPE_IDS) {
    const row = types[id];
    const windows = clampNonNegative(row.windows);
    const blinds = Math.min(clampNonNegative(row.blinds), windows);
    const weight = WINDOW_TYPE_WEIGHTS[id];
    equivalentWindows += windows * weight;
    blindsUnits += blinds * weight;
  }

  return { equivalentWindows, blindsUnits };
}

export function sumAreaEquivalentWindows(area: WindowAreaQuantities): {
  equivalentWindows: number;
  blindsUnits: number;
} {
  const windowSqm = clampNonNegative(area.windowSqm);
  const blindsSqm = Math.min(clampNonNegative(area.blindsSqm), windowSqm);
  return {
    equivalentWindows: windowSqm / SQM_PER_STANDARD_WINDOW,
    blindsUnits: blindsSqm / SQM_PER_STANDARD_WINDOW,
  };
}

/** Fill a partial per-type breakdown with safe zeroes. */
export function normalizeWindowTypeQuantities(
  input?: Partial<Record<WindowTypeId, WindowTypeQuantity>>,
): WindowTypeQuantities {
  return WINDOW_TYPE_IDS.reduce((acc, id) => {
    const row = input?.[id];
    acc[id] = {
      windows: clampNonNegative(row?.windows ?? 0),
      blinds: clampNonNegative(row?.blinds ?? 0),
    };
    return acc;
  }, {} as WindowTypeQuantities);
}

/**
 * Resolve the configurator mode. Falls back to the mode implied by whichever
 * breakdown is present so partial payloads still price correctly.
 */
export function resolveWindowCleaningMode(input: {
  mode?: WindowPricingMode;
  types?: Partial<Record<WindowTypeId, WindowTypeQuantity>>;
  area?: WindowAreaQuantities;
}): WindowPricingMode {
  if (input.mode) {
    return input.mode;
  }
  if (input.area && !input.types) {
    return 'area';
  }
  return 'type';
}

/**
 * Resolve the bookable window count and the blind units from a configurator
 * breakdown. Shared by the marketing calculator and the booking flow so both
 * count a window the same way.
 */
export function resolveWindowCleaningQuantities(input: {
  mode?: WindowPricingMode;
  types?: Partial<Record<WindowTypeId, WindowTypeQuantity>>;
  area?: WindowAreaQuantities;
}): { windowCount: number; blindsUnits: number } {
  if (resolveWindowCleaningMode(input) === 'area') {
    const { equivalentWindows, blindsUnits } = sumAreaEquivalentWindows({
      windowSqm: input.area?.windowSqm ?? 0,
      blindsSqm: input.area?.blindsSqm ?? 0,
    });
    return { windowCount: equivalentWindows, blindsUnits };
  }

  const { equivalentWindows, blindsUnits } = sumTypeEquivalentWindows(
    normalizeWindowTypeQuantities(input.types),
  );
  return { windowCount: equivalentWindows, blindsUnits };
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Build a live estimate from calculator inputs using public window unit pricing.
 */
export function calculateWindowCleaningEstimate(
  input: WindowCalculatorPricingInput,
): WindowCalculatorEstimate {
  const pricePerWindow = Math.max(0, input.pricePerWindow);
  const minimumWindows = Math.max(1, Math.floor(input.minimumWindows));
  const minimumPrice = Math.max(0, input.minimumPrice);

  const { equivalentWindows, blindsUnits } =
    input.mode === 'type'
      ? sumTypeEquivalentWindows(input.types)
      : sumAreaEquivalentWindows(input.area);

  const isEmpty = equivalentWindows <= 0 && blindsUnits <= 0;

  const addOnsNet = roundMoney(
    (input.addOns ?? []).reduce((sum, line) => {
      const qty = clampNonNegative(line.quantity);
      const unit = Math.max(0, line.unitPrice);
      return sum + qty * unit;
    }, 0),
  );

  if (isEmpty) {
    return {
      equivalentWindows: 0,
      blindsUnits: 0,
      bookedWindows: 0,
      windowsNet: 0,
      blindsNet: 0,
      renovationExtraNet: 0,
      addOnsNet,
      subtotalNet: addOnsNet,
      vat: roundMoney(addOnsNet * WINDOW_CALCULATOR_VAT_RATE),
      totalGross: roundMoney(addOnsNet * (1 + WINDOW_CALCULATOR_VAT_RATE)),
      meetsMinimum: false,
      isEmpty: true,
    };
  }

  const windowsNetRaw = equivalentWindows * pricePerWindow;
  const blindsNet = roundMoney(blindsUnits * pricePerWindow * BLINDS_SURCHARGE_RATIO);
  const glassBeforeRenovation = windowsNetRaw + blindsNet;
  const renovationExtraNet = input.afterRenovation
    ? roundMoney(glassBeforeRenovation * (AFTER_RENOVATION_MULTIPLIER - 1))
    : 0;

  let windowsNet = roundMoney(windowsNetRaw);
  let glassSubtotal = roundMoney(windowsNet + blindsNet + renovationExtraNet);

  // Enforce public minimum order on the glass portion (before optional add-ons).
  if (glassSubtotal < minimumPrice) {
    glassSubtotal = minimumPrice;
    windowsNet = roundMoney(Math.max(0, glassSubtotal - blindsNet - renovationExtraNet));
  }

  const subtotalNet = roundMoney(glassSubtotal + addOnsNet);
  const vat = roundMoney(subtotalNet * WINDOW_CALCULATOR_VAT_RATE);
  const totalGross = roundMoney(subtotalNet + vat);
  const bookedWindows = Math.max(minimumWindows, Math.ceil(equivalentWindows));

  return {
    equivalentWindows: roundMoney(equivalentWindows),
    blindsUnits: roundMoney(blindsUnits),
    bookedWindows,
    windowsNet,
    blindsNet,
    renovationExtraNet,
    addOnsNet,
    subtotalNet,
    vat,
    totalGross,
    meetsMinimum:
      equivalentWindows + blindsUnits >= minimumWindows || glassSubtotal >= minimumPrice,
    isEmpty: false,
  };
}
